// ============================================================================
//  MOT CADEAU (ENVELOPPE) — logique panier. Remplace l'app payante LDT Gift Wrap.
//  Binôme du bloc blocks/gift-message.liquid (qui contient l'interface).
// ============================================================================
//
//  PRINCIPE
//  Le thème Horizon ajoute le produit principal au panier via le composant
//  <product-form-component> (assets/product-form.js). On NE réécrit PAS cet
//  ajout : on l'écoute. Quand le produit principal vient d'être ajouté, et si
//  le client a coché la case + écrit un mot, on ajoute EN PLUS l'enveloppe
//  payante au panier, avec le nom et le message en propriétés de ligne.
//
//  POINTS D'ACCROCHE AVEC LE THÈME (à re-vérifier après une MAJ Shopify) :
//  - product-form.js émet, après un ajout réussi, un CartAddEvent. ATTENTION :
//    cet événement porte le NOM `cart:update` (= ThemeEvents.cartUpdate), avec
//    dans event.detail.data : { source:'product-form-component', productId, ... }.
//    C'est ce qu'on filtre ci-dessous. Si Shopify renomme cet événement ou
//    change ces champs, c'est ICI qu'il faudra adapter.
//  - On rappelle `Theme.routes.cart_add_url` et `Theme.routes.cart_url`
//    (définis dans snippets/scripts.liquid) pour parler au panier.
//  - Après avoir ajouté l'enveloppe, on émet un CartUpdateEvent SANS `sections` :
//    les composants panier (tiroir, pastille) se rafraîchissent alors d'eux-mêmes
//    en re-fetchant leur section. Notre source 'gift-message-component' n'est PAS
//    'product-form-component', donc notre propre événement ne se redéclenche pas
//    (pas de boucle infinie). Le drapeau #busy ajoute une sécurité.
//
import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * @typedef {object} GiftMessageRefs
 * @property {HTMLInputElement} [enableCheckbox] - La case "ajouter l'enveloppe".
 * @property {HTMLElement} [field] - Le volet révélé quand la case est cochée.
 * @property {HTMLInputElement} [senderInput] - Le champ "De la part de".
 * @property {HTMLTextAreaElement} [textInput] - Le champ "Message".
 * @property {HTMLElement} [characterCount] - Le compteur de caractères.
 *
 * @extends Component<GiftMessageRefs>
 */
class GiftMessageComponent extends Component {
  #abortController = new AbortController();
  /** @type {boolean} Empêche un double-ajout pendant que l'enveloppe s'ajoute. */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    // On écoute TOUS les `cart:update` du document. Le filtrage (est-ce bien
    // l'ajout du produit principal de CE bloc ?) se fait dans #onCartUpdate.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Appelée quand on coche/décoche la case (on:change="/handleToggle").
   * Ouvre ou ferme le volet. On NE met PAS le focus automatiquement sur le
   * champ message : sur mobile, ça faisait surgir le clavier (et zoomer
   * l'écran) dès l'ouverture. Le client tape quand il le souhaite.
   */
  handleToggle() {
    const { enableCheckbox, field, textInput } = this.refs;
    const checked = Boolean(enableCheckbox?.checked);

    field?.toggleAttribute('hidden', !checked);
    if (textInput) {
      textInput.required = checked;
      if (checked) this.#updateCharacterCount();
    }
  }

  /** Appelée à chaque frappe dans le message (on:input="/handleInput"). */
  handleInput() {
    this.#updateCharacterCount();
  }

  /** Met à jour le texte "X / max" du compteur de caractères. */
  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    characterCount.textContent = template
      .replace('[current]', textInput.value.length.toString())
      .replace('[max]', textInput.maxLength.toString());
  }

  /**
   * Réagit à l'ajout du produit principal au panier et ajoute l'enveloppe si
   * le client a opté pour le mot cadeau.
   * @param {CustomEvent} event - Un `cart:update` (voir l'en-tête du fichier).
   */
  #onCartUpdate = async (event) => {
    const data = /** @type {any} */ (event).detail?.data;

    // On ne réagit QU'À l'ajout fait par le formulaire produit — pas à notre
    // propre ajout d'enveloppe, ni aux modifications de panier, ni à une erreur.
    // (Ce filtre évite aussi toute boucle infinie.)
    if (!data || data.source !== 'product-form-component' || data.didError) return;

    // …et seulement pour LE produit de ce bloc (plusieurs produits/formulaires
    // peuvent coexister sur une même page).
    if (String(data.productId) !== String(this.dataset.productId)) return;
    if (this.#busy) return;

    const { enableCheckbox, textInput, senderInput } = this.refs;
    if (!enableCheckbox?.checked) return;

    const message = (textInput?.value || '').trim();
    const sender = (senderInput?.value || '').trim();
    // On ajoute l'enveloppe seulement si le client a écrit quelque chose.
    if (!message && !sender) return;

    const variantId = Number(this.dataset.variantId);
    if (!variantId) return;

    this.#busy = true;
    try {
      // Propriétés de ligne affichées sur la commande. On N'AJOUTE PAS le nom du
      // produit : il ne doit pas apparaître sous le mot, pour éviter qu'il soit
      // recopié par erreur sur la carte au moment de la préparation du colis.
      const properties = {};
      if (sender) {
        properties[this.dataset.senderName || 'De la part de'] = sender;
      }
      if (message) {
        properties[this.dataset.messageName || 'Message'] = message;
      }

      // Ajout de l'enveloppe au panier (endpoint AJAX standard Shopify).
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties }),
      });
      const added = await addResponse.json();

      if (added.status) {
        console.error('Gift message : échec de l\'ajout de l\'enveloppe', added);
        return;
      }

      // On récupère le panier à jour et on émet un `cart:update` : sans charge
      // `sections`, les composants panier re-fetchent leur section et affichent
      // donc la nouvelle ligne (l'enveloppe).
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'gift-message-component', {
          source: 'gift-message-component',
        })
      );
    } catch (error) {
      console.error('Gift message : erreur lors de l\'ajout de l\'enveloppe', error);
    } finally {
      this.#busy = false;
    }
  };
}

if (!customElements.get('gift-message-component')) {
  customElements.define('gift-message-component', GiftMessageComponent);
}
